<template>
	<div v-if="hasData" class="kpi-history-chart mt-6">
		<h3 class="kpi-history-chart-title mb-4">Your KPI score over evaluations</h3>
		<div class="kpi-history-chart-container">
			<canvas ref="chartCanvas"></canvas>
		</div>
	</div>
</template>

<script>
import Chart from "chart.js"
import ChartDataLabels from "chartjs-plugin-datalabels"
import { buildKpiHistoryChartEntries } from "../utils/supplierKpiHistoryChart"

Chart.plugins.register(ChartDataLabels)

const CHART_COLORS = {
	delivery: "#2e7d32",
	quality: "#9b8fd9",
	support: "#c67b4a",
}

export default {
	name: "SupplierKpiHistoryChart",
	props: {
		history: {
			type: Array,
			default: () => [],
		},
		maxEntries: {
			type: Number,
			default: 5,
		},
	},
	data() {
		return {
			chart: null,
		}
	},
	computed: {
		chartEntries() {
			return buildKpiHistoryChartEntries(this.history, this.maxEntries)
		},
		hasData() {
			return this.chartEntries.length > 0
		},
	},
	watch: {
		history: {
			deep: true,
			handler() {
				this.renderChart()
			},
		},
	},
	mounted() {
		this.renderChart()
	},
	beforeDestroy() {
		if (this.chart) {
			this.chart.destroy()
		}
	},
	methods: {
		renderChart() {
			if (!this.hasData || !this.$refs.chartCanvas) {
				if (this.chart) {
					this.chart.destroy()
					this.chart = null
				}
				return
			}

			const labels = this.chartEntries.map((entry) => entry.label)
			const datasets = [
				{
					label: "Delivery",
					data: this.chartEntries.map((entry) => entry.delivery),
					backgroundColor: CHART_COLORS.delivery,
				},
				{
					label: "Quality",
					data: this.chartEntries.map((entry) => entry.quality),
					backgroundColor: CHART_COLORS.quality,
				},
				{
					label: "Support",
					data: this.chartEntries.map((entry) => entry.support),
					backgroundColor: CHART_COLORS.support,
				},
			]

			if (this.chart) {
				this.chart.destroy()
			}

			this.chart = new Chart(this.$refs.chartCanvas.getContext("2d"), {
				type: "bar",
				plugins: [ChartDataLabels],
				data: { labels, datasets },
				options: {
					responsive: true,
					maintainAspectRatio: false,
					layout: {
						padding: {
							top: 8,
							bottom: 4,
						},
					},
					legend: {
						display: true,
						position: "bottom",
						labels: {
							boxWidth: 12,
							padding: 18,
							fontSize: 12,
						},
					},
					tooltips: {
						mode: "index",
						intersect: false,
					},
					scales: {
						xAxes: [{
							gridLines: { display: false },
							barPercentage: 1,
							categoryPercentage: 0.45,
						}],
						yAxes: [{
							ticks: {
								beginAtZero: true,
								min: 0,
								max: 6,
								stepSize: 1,
								callback(value) {
									return value <= 5 ? value : ""
								},
							},
							gridLines: {
								color: "rgba(0, 0, 0, 0.06)",
							},
							scaleLabel: {
								display: true,
								labelString: "KPI score (1–5)",
								fontSize: 12,
							},
						}],
					},
					plugins: {
						datalabels: {
							anchor: "end",
							align: "top",
							offset: 4,
							clamp: true,
							color: "#424242",
							font: {
								size: 11,
								weight: "600",
							},
							formatter(value) {
								return value === null || value === undefined ? "" : value.toFixed(1)
							},
						},
					},
				},
			})
		},
	},
}
</script>

<style scoped>
.kpi-history-chart-title {
	font-size: 1rem;
	font-weight: 700;
	color: #1a1a1a;
	text-align: center;
}

.kpi-history-chart-container {
	position: relative;
	height: 340px;
	width: 100%;
	padding-bottom: 8px;
}
</style>
